import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  MessageCircle, 
  Clock, 
  Users, 
  Waves, 
  Utensils, 
  Wine,
  Anchor,
  Sun,
  ChevronRight
} from 'lucide-react';
import { useLocation } from '../context/LocationContext';
import { useLanguage } from '../context/LanguageContext';

// Curated recommendations data - hyper-local to Monopoli and Fasano
const CURATED_RECOMMENDATIONS = {
  monopoli: {
    morning: [
      {
        id: 'porto-bianco',
        name: 'Porto Bianco Beach',
        type: 'beach',
        whyRecommend: {
          en: "Quiet morning spot with calm waters — locals' favorite before 10am",
          it: "Posto tranquillo al mattino con acque calme — preferito dai locali prima delle 10",
          fr: "Endroit calme le matin avec eaux calmes — favori des locaux avant 10h",
          de: "Ruhiger Morgenplatz mit ruhigem Wasser — Lieblingsort der Einheimischen vor 10 Uhr"
        },
        price: { en: 'Free', it: 'Gratuito', fr: 'Gratuit', de: 'Kostenlos' },
        vibe: ['quiet', 'local', 'swimming'],
        crowdLevel: 'low',
        bestFor: ['couple', 'luxury'],
        image: 'https://images.pexels.com/photos/36430125/pexels-photo-36430125.jpeg?w=600',
        whatsapp: '393331234567'
      },
      {
        id: 'cala-porta-vecchia',
        name: 'Cala Porta Vecchia',
        type: 'beach',
        whyRecommend: {
          en: "The most photogenic cove in Monopoli — arrive early for the best spot",
          it: "La cala più fotogenica di Monopoli — arriva presto per il posto migliore",
          fr: "La crique la plus photogénique de Monopoli — arrivez tôt pour la meilleure place",
          de: "Die fotogenste Bucht in Monopoli — früh ankommen für den besten Platz"
        },
        price: { en: 'Free', it: 'Gratuito', fr: 'Gratuit', de: 'Kostenlos' },
        vibe: ['scenic', 'photography', 'snorkeling'],
        crowdLevel: 'medium',
        bestFor: ['couple', 'family'],
        image: 'https://images.pexels.com/photos/36430128/pexels-photo-36430128.jpeg?w=600',
        whatsapp: '393331234567'
      }
    ],
    lunch: [
      {
        id: 'la-locanda',
        name: 'La Locanda dei Pescatori',
        type: 'restaurant',
        whyRecommend: {
          en: "Fresh catch of the day, right on the harbor — ask for the crudo tasting",
          it: "Pescato fresco del giorno, proprio sul porto — chiedi la degustazione di crudo",
          fr: "Prise fraîche du jour, sur le port — demandez la dégustation de cru",
          de: "Frischer Tagesfang, direkt am Hafen — fragen Sie nach der Crudo-Verkostung"
        },
        price: { en: '€€', it: '€€', fr: '€€', de: '€€' },
        vibe: ['seafood', 'harbor-view', 'authentic'],
        crowdLevel: 'medium',
        bestFor: ['couple', 'family', 'luxury'],
        image: 'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=600',
        whatsapp: '393331234567',
        avgPrice: '35-50'
      },
      {
        id: 'radici',
        name: 'Radici',
        type: 'restaurant',
        whyRecommend: {
          en: "Farm-to-table Puglian cuisine — the orecchiette is life-changing",
          it: "Cucina pugliese dal campo alla tavola — le orecchiette cambiano la vita",
          fr: "Cuisine des Pouilles de la ferme à la table — les orecchiette changent la vie",
          de: "Apulische Küche vom Bauernhof auf den Tisch — die Orecchiette sind lebensverändernd"
        },
        price: { en: '€€', it: '€€', fr: '€€', de: '€€' },
        vibe: ['local', 'traditional', 'cozy'],
        crowdLevel: 'high',
        bestFor: ['couple', 'budget'],
        image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=600',
        whatsapp: '393331234567',
        avgPrice: '25-40'
      }
    ],
    afternoon: [
      {
        id: 'boat-tour-monopoli',
        name: 'Private Boat Tour',
        type: 'experience',
        whyRecommend: {
          en: "Explore hidden caves and swim in crystal waters — 3 hours, includes Prosecco",
          it: "Esplora grotte nascoste e nuota in acque cristalline — 3 ore, include Prosecco",
          fr: "Explorez des grottes cachées et nagez dans des eaux cristallines — 3h, Prosecco inclus",
          de: "Entdecken Sie versteckte Höhlen und schwimmen Sie in kristallklarem Wasser — 3 Std., inkl. Prosecco"
        },
        price: { en: 'From €180', it: 'Da €180', fr: 'À partir de €180', de: 'Ab €180' },
        vibe: ['adventure', 'romantic', 'exclusive'],
        crowdLevel: 'private',
        bestFor: ['couple', 'luxury'],
        image: 'https://images.unsplash.com/photo-1654616481046-3e399e4089dd?w=600',
        whatsapp: '393331234567'
      },
      {
        id: 'lido-santo-stefano',
        name: 'Lido Santo Stefano',
        type: 'beach-club',
        whyRecommend: {
          en: "Best beach club in Monopoli — reserve sunbeds, great aperitivo at sunset",
          it: "Miglior stabilimento di Monopoli — prenota lettini, ottimo aperitivo al tramonto",
          fr: "Meilleur club de plage de Monopoli — réservez transats, super apéro au coucher du soleil",
          de: "Bester Strandclub in Monopoli — Liegen reservieren, toller Aperitivo bei Sonnenuntergang"
        },
        price: { en: '€40-60/day', it: '€40-60/giorno', fr: '€40-60/jour', de: '€40-60/Tag' },
        vibe: ['relaxing', 'stylish', 'sunset'],
        crowdLevel: 'medium',
        bestFor: ['couple', 'luxury', 'family'],
        image: 'https://images.unsplash.com/photo-1597477765693-c51ce4dfab0c?w=600',
        whatsapp: '393331234567'
      }
    ],
    dinner: [
      {
        id: 'carlo-quinto',
        name: 'Carlo Quinto',
        type: 'restaurant',
        whyRecommend: {
          en: "Rooftop terrace in the historic center — book the sunset table",
          it: "Terrazza sul tetto nel centro storico — prenota il tavolo al tramonto",
          fr: "Terrasse sur le toit dans le centre historique — réservez la table au coucher du soleil",
          de: "Dachterrasse im historischen Zentrum — reservieren Sie den Sonnenuntergangstisch"
        },
        price: { en: '€€€', it: '€€€', fr: '€€€', de: '€€€' },
        vibe: ['romantic', 'fine-dining', 'views'],
        crowdLevel: 'high',
        bestFor: ['couple', 'luxury'],
        image: 'https://images.unsplash.com/photo-1550966871-3ed3cdb5ed0c?w=600',
        whatsapp: '393331234567',
        avgPrice: '60-90'
      }
    ]
  },
  fasano: {
    morning: [
      {
        id: 'borgo-egnazia-beach',
        name: 'Borgo Egnazia Beach Club',
        type: 'beach-club',
        whyRecommend: {
          en: "Five-star beach experience — reserve a private cabana for ultimate luxury",
          it: "Esperienza balneare a cinque stelle — prenota una cabana privata per il massimo lusso",
          fr: "Expérience de plage cinq étoiles — réservez une cabane privée pour un luxe ultime",
          de: "Fünf-Sterne-Stranderlebnis — reservieren Sie eine private Cabana für ultimativen Luxus"
        },
        price: { en: 'From €150/day', it: 'Da €150/giorno', fr: 'À partir de €150/jour', de: 'Ab €150/Tag' },
        vibe: ['luxury', 'exclusive', 'spa'],
        crowdLevel: 'low',
        bestFor: ['couple', 'luxury'],
        image: 'https://images.unsplash.com/photo-1596178060671-7a80dc8059ea?w=600',
        whatsapp: '393339876543'
      },
      {
        id: 'torre-canne',
        name: 'Torre Canne Beach',
        type: 'beach',
        whyRecommend: {
          en: "Long sandy beach with thermal springs nearby — perfect for families",
          it: "Lunga spiaggia sabbiosa con terme vicine — perfetta per famiglie",
          fr: "Longue plage de sable avec sources thermales à proximité — parfait pour les familles",
          de: "Langer Sandstrand mit Thermalquellen in der Nähe — perfekt für Familien"
        },
        price: { en: 'Free', it: 'Gratuito', fr: 'Gratuit', de: 'Kostenlos' },
        vibe: ['sandy', 'family', 'spacious'],
        crowdLevel: 'medium',
        bestFor: ['family', 'budget'],
        image: 'https://images.pexels.com/photos/36604918/pexels-photo-36604918.jpeg?w=600',
        whatsapp: '393339876543'
      }
    ],
    lunch: [
      {
        id: 'masseria-torre-coccaro',
        name: 'Masseria Torre Coccaro',
        type: 'restaurant',
        whyRecommend: {
          en: "Michelin-quality lunch in a 16th century masseria — their olive oil is legendary",
          it: "Pranzo di qualità Michelin in una masseria del XVI secolo — il loro olio d'oliva è leggendario",
          fr: "Déjeuner de qualité Michelin dans une masseria du 16e siècle — leur huile d'olive est légendaire",
          de: "Mittagessen mit Michelin-Qualität in einer Masseria aus dem 16. Jh. — ihr Olivenöl ist legendär"
        },
        price: { en: '€€€€', it: '€€€€', fr: '€€€€', de: '€€€€' },
        vibe: ['luxury', 'historic', 'gourmet'],
        crowdLevel: 'low',
        bestFor: ['couple', 'luxury'],
        image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=600',
        whatsapp: '393339876543',
        avgPrice: '80-120'
      },
      {
        id: 'il-frantoio',
        name: 'Il Frantoio',
        type: 'restaurant',
        whyRecommend: {
          en: "Historic olive mill turned farm restaurant — the antipasti spread is unforgettable",
          it: "Antico frantoio trasformato in agriturismo — l'antipasto è indimenticabile",
          fr: "Ancien moulin à huile devenu restaurant de ferme — l'assortiment d'antipasti est inoubliable",
          de: "Historische Ölmühle zum Bauernhofrestaurant geworden — die Antipasti-Auswahl ist unvergesslich"
        },
        price: { en: '€€€', it: '€€€', fr: '€€€', de: '€€€' },
        vibe: ['authentic', 'farm', 'rustic-chic'],
        crowdLevel: 'medium',
        bestFor: ['couple', 'family', 'luxury'],
        image: 'https://images.unsplash.com/photo-1552566626-52f8b828add9?w=600',
        whatsapp: '393339876543',
        avgPrice: '50-70'
      }
    ],
    afternoon: [
      {
        id: 'private-chef',
        name: 'Private Chef Experience',
        type: 'experience',
        whyRecommend: {
          en: "A local chef comes to you — learn to make fresh pasta with wine pairing",
          it: "Uno chef locale viene da te — impara a fare la pasta fresca con abbinamento vini",
          fr: "Un chef local vient chez vous — apprenez à faire des pâtes fraîches avec accord vins",
          de: "Ein lokaler Koch kommt zu Ihnen — lernen Sie frische Pasta mit Weinbegleitung"
        },
        price: { en: 'From €200/person', it: 'Da €200/persona', fr: 'À partir de €200/personne', de: 'Ab €200/Person' },
        vibe: ['exclusive', 'culinary', 'intimate'],
        crowdLevel: 'private',
        bestFor: ['couple', 'luxury'],
        image: 'https://images.unsplash.com/photo-1556910103-1c02745aae4d?w=600',
        whatsapp: '393339876543'
      },
      {
        id: 'spa-experience',
        name: 'Masseria Spa Day',
        type: 'experience',
        whyRecommend: {
          en: "Full day spa in a 500-year-old masseria — includes olive oil treatments",
          it: "Spa giornaliera in una masseria di 500 anni — include trattamenti all'olio d'oliva",
          fr: "Spa d'une journée dans une masseria de 500 ans — inclut soins à l'huile d'olive",
          de: "Ganztägiges Spa in einer 500 Jahre alten Masseria — inkl. Olivenöl-Behandlungen"
        },
        price: { en: 'From €150', it: 'Da €150', fr: 'À partir de €150', de: 'Ab €150' },
        vibe: ['wellness', 'relaxing', 'luxury'],
        crowdLevel: 'low',
        bestFor: ['couple', 'luxury'],
        image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=600',
        whatsapp: '393339876543'
      }
    ],
    dinner: [
      {
        id: 'due-camini',
        name: 'Due Camini',
        type: 'restaurant',
        whyRecommend: {
          en: "Two Michelin stars in Borgo Egnazia — book 2 weeks ahead, worth every euro",
          it: "Due stelle Michelin a Borgo Egnazia — prenota 2 settimane prima, vale ogni euro",
          fr: "Deux étoiles Michelin à Borgo Egnazia — réservez 2 semaines à l'avance, vaut chaque euro",
          de: "Zwei Michelin-Sterne in Borgo Egnazia — 2 Wochen im Voraus buchen, jeden Euro wert"
        },
        price: { en: '€€€€', it: '€€€€', fr: '€€€€', de: '€€€€' },
        vibe: ['michelin', 'exclusive', 'tasting-menu'],
        crowdLevel: 'low',
        bestFor: ['couple', 'luxury'],
        image: 'https://images.unsplash.com/photo-1559339352-11d035aa65de?w=600',
        whatsapp: '393339876543',
        avgPrice: '150-200'
      }
    ]
  }
};

const getTimeOfDay = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'morning';
  if (hour < 15) return 'lunch';
  if (hour < 19) return 'afternoon';
  return 'dinner';
};

const getIcon = (type) => {
  const icons = {
    beach: Waves,
    'beach-club': Sun,
    restaurant: Utensils,
    experience: Anchor,
    wine: Wine
  };
  return icons[type] || Sparkles;
};

const getCrowdLabel = (level, lang) => {
  const labels = {
    low: { en: 'Quiet now', it: 'Tranquillo ora', fr: 'Calme maintenant', de: 'Ruhig jetzt' },
    medium: { en: 'Getting busy', it: 'Si sta riempiendo', fr: 'Ça se remplit', de: 'Wird voller' },
    high: { en: 'Popular', it: 'Popolare', fr: 'Populaire', de: 'Beliebt' },
    private: { en: 'Private', it: 'Privato', fr: 'Privé', de: 'Privat' }
  };
  return labels[level]?.[lang] || labels[level]?.en || level;
};

export default function BestRightNow() {
  const { location, guestType } = useLocation();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [timeOfDay, setTimeOfDay] = useState(getTimeOfDay());

  const translations = {
    en: {
      title: "Best Options Right Now",
      subtitle: "Curated picks based on your preferences",
      whyWeRecommend: "Why we recommend it",
      contact: "Contact on WhatsApp",
      request: "Request availability",
      viewAll: "View all options",
      morning: "This morning",
      lunch: "For lunch",
      afternoon: "This afternoon", 
      dinner: "For dinner"
    },
    it: {
      title: "Le Migliori Opzioni Ora",
      subtitle: "Selezioni curate in base alle tue preferenze",
      whyWeRecommend: "Perché lo consigliamo",
      contact: "Contatta su WhatsApp",
      request: "Richiedi disponibilità",
      viewAll: "Vedi tutte le opzioni",
      morning: "Questa mattina",
      lunch: "Per pranzo",
      afternoon: "Questo pomeriggio",
      dinner: "Per cena"
    },
    fr: {
      title: "Meilleures Options Maintenant",
      subtitle: "Sélections selon vos préférences",
      whyWeRecommend: "Pourquoi nous le recommandons",
      contact: "Contacter sur WhatsApp",
      request: "Demander disponibilité",
      viewAll: "Voir toutes les options",
      morning: "Ce matin",
      lunch: "Pour le déjeuner",
      afternoon: "Cet après-midi",
      dinner: "Pour le dîner"
    },
    de: {
      title: "Beste Optionen Jetzt",
      subtitle: "Kuratierte Auswahl nach Ihren Vorlieben",
      whyWeRecommend: "Warum wir es empfehlen",
      contact: "Auf WhatsApp kontaktieren",
      request: "Verfügbarkeit anfragen",
      viewAll: "Alle Optionen anzeigen",
      morning: "Heute Morgen",
      lunch: "Zum Mittagessen",
      afternoon: "Heute Nachmittag",
      dinner: "Zum Abendessen"
    }
  };

  const txt = translations[language] || translations.en;

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeOfDay(getTimeOfDay());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  if (!location) return null;

  const recommendations = CURATED_RECOMMENDATIONS[location]?.[timeOfDay] || [];
  
  // Filter and sort by guest type preference
  const filteredRecs = recommendations
    .filter(rec => !guestType || rec.bestFor.includes(guestType))
    .slice(0, 3);

  const handleWhatsApp = (rec) => {
    const message = encodeURIComponent(`Hi! I'm interested in ${rec.name}. Can you help me with availability?`);
    window.open(`https://wa.me/${rec.whatsapp}?text=${message}`, '_blank');
  };

  return (
    <section className="px-6 py-8 bg-sand-100" data-testid="best-right-now">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-gradient-to-br from-olive-500 to-olive-600 rounded-xl flex items-center justify-center shadow-md shadow-olive-600/20">
            <Sparkles size={20} className="text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-serif text-stone-800">{txt.title}</h2>
            <p className="text-stone-500 text-sm">{txt[timeOfDay]} · {txt.subtitle}</p>
          </div>
        </div>

        <div className="space-y-4 mt-6">
          {filteredRecs.map((rec, index) => {
            const Icon = getIcon(rec.type);
            return (
              <article 
                key={rec.id}
                className="bg-white rounded-2xl overflow-hidden border border-sand-300 shadow-sm hover:shadow-lg hover:shadow-olive-600/10 transition-shadow"
                data-testid={`recommendation-${rec.id}`}
              >
                <div className="flex">
                  <div className="w-32 h-32 md:w-40 md:h-40 flex-shrink-0">
                    <img 
                      src={rec.image} 
                      alt={`${rec.name} - ${rec.whyRecommend[language] || rec.whyRecommend.en}`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="flex-1 p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Icon size={14} className="text-olive-600" />
                          <span className="text-xs text-olive-600 font-medium uppercase tracking-wide">
                            {rec.type.replace('-', ' ')}
                          </span>
                        </div>
                        <h3 className="font-semibold text-stone-800">{rec.name}</h3>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-sea-700">{rec.price[language] || rec.price.en}</p>
                        <div className="flex items-center gap-1 text-xs text-stone-500 mt-1">
                          <Users size={12} />
                          <span>{getCrowdLabel(rec.crowdLevel, language)}</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-sm text-stone-600 mt-2 line-clamp-2">
                      {rec.whyRecommend[language] || rec.whyRecommend.en}
                    </p>

                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {rec.vibe.slice(0, 3).map((v, i) => (
                        <span key={i} className="text-xs px-2 py-0.5 bg-sand-200 text-stone-600 rounded-full">
                          {v}
                        </span>
                      ))}
                    </div>

                    <button
                      onClick={() => handleWhatsApp(rec)}
                      className="mt-3 w-full bg-olive-600 hover:bg-olive-700 text-white py-2 rounded-xl text-sm font-medium transition-colors flex items-center justify-center gap-2 shadow-sm"
                    >
                      <MessageCircle size={14} />
                      {txt.contact}
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {filteredRecs.length === 0 && (
          <div className="text-center py-8 text-stone-500">
            <p>No recommendations available for your current preferences.</p>
          </div>
        )}
      </div>
    </section>
  );
}
